import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowRightLeft, Search, X } from 'lucide-react';
import { useCRM } from '../../utils/CRMContext';
import type { EnhancedDeal } from '../../types';
import {
  TransferDealToLeadError,
  getTransferDealWarnings,
} from '../../utils/transferDealToLead';
import { getProjectDisplayName } from '../../utils/projectName';
import './TransferDealToLeadModal.css';

export interface TransferDealToLeadModalProps {
  deal: EnhancedDeal;
  userId: string;
  onClose: () => void;
  onSuccess?: (result: { targetLeadId: string }) => void;
}

export const TransferDealToLeadModal: React.FC<TransferDealToLeadModalProps> = ({
  deal,
  userId,
  onClose,
  onSuccess,
}) => {
  const { leads, timberProjects, transferDealToLead } = useCRM();
  const [search, setSearch] = useState('');
  const [targetLeadId, setTargetLeadId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const sourceLead = useMemo(
    () => (deal.leadId ? leads.find((l) => l.id === deal.leadId) : undefined),
    [deal.leadId, leads],
  );

  const projectLabel = useMemo(
    () =>
      getProjectDisplayName({
        projectNumber: deal.projectNumber,
        clientLabel: deal.clientLabel,
        descriptor: deal.descriptor,
        title: deal.title,
      }) || deal.title,
    [deal],
  );

  const timberCount = useMemo(
    () => timberProjects.filter((p) => p.dealId === deal.id).length,
    [timberProjects, deal.id],
  );

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads
      .filter((l) => !l.archived && l.id !== deal.leadId)
      .filter((l) => {
        if (!q) return true;
        const name = `${l.firstName} ${l.lastName}`.toLowerCase();
        const email = (l.email || '').toLowerCase();
        const company = (l.company || '').toLowerCase();
        return name.includes(q) || email.includes(q) || company.includes(q);
      })
      .slice(0, 40);
  }, [leads, deal.leadId, search]);

  const targetLead = useMemo(
    () => (targetLeadId ? leads.find((l) => l.id === targetLeadId) : undefined),
    [targetLeadId, leads],
  );

  useEffect(() => {
    if (!targetLead || !deal.id) {
      setWarnings([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const w = await getTransferDealWarnings(deal, targetLead, timberCount);
      if (!cancelled) setWarnings(w);
    })();
    return () => {
      cancelled = true;
    };
  }, [targetLead, deal, timberCount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal.id || !targetLeadId) {
      setError('Select a target lead.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await transferDealToLead(deal.id, targetLeadId, userId);
      onSuccess?.({ targetLeadId: result.targetLeadId });
      onClose();
    } catch (err) {
      const message =
        err instanceof TransferDealToLeadError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Transfer failed.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="transfer-deal-modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="transfer-deal-modal"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="transfer-deal-title"
      >
        <div className="transfer-deal-modal-header">
          <div className="transfer-deal-modal-title-row">
            <ArrowRightLeft size={18} />
            <h2 id="transfer-deal-title">Transfer project to another lead</h2>
          </div>
          <button type="button" className="transfer-deal-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <p className="transfer-deal-modal-sub">
          Move <strong>{projectLabel}</strong>
          {sourceLead ? (
            <>
              {' '}
              from <strong>{sourceLead.firstName} {sourceLead.lastName}</strong>
            </>
          ) : null}{' '}
          to a different lead. Deal-scoped documents, time, portal data, and annotations move with it.
          Lead comments, todos, and SMS stay on the original lead.
        </p>

        <form onSubmit={handleSubmit} className="transfer-deal-modal-form">
          <label className="transfer-deal-modal-label" htmlFor="transfer-deal-search">
            Target lead
          </label>
          <div className="transfer-deal-search-wrap">
            <Search size={16} />
            <input
              id="transfer-deal-search"
              type="search"
              placeholder="Search by name, email, or company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="transfer-deal-lead-list" role="listbox" aria-label="Target leads">
            {candidates.length === 0 ? (
              <p className="transfer-deal-empty">No matching leads.</p>
            ) : (
              candidates.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  role="option"
                  aria-selected={targetLeadId === l.id}
                  className={`transfer-deal-lead-option${targetLeadId === l.id ? ' selected' : ''}`}
                  onClick={() => setTargetLeadId(l.id!)}
                >
                  <span className="transfer-deal-lead-name">
                    {l.firstName} {l.lastName}
                  </span>
                  <span className="transfer-deal-lead-meta">
                    {[l.email, l.company].filter(Boolean).join(' · ')}
                  </span>
                </button>
              ))
            )}
          </div>

          {warnings.length > 0 && (
            <div className="transfer-deal-warnings">
              <AlertTriangle size={16} />
              <ul>
                {warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {error && <p className="transfer-deal-error">{error}</p>}

          <div className="transfer-deal-modal-actions">
            <button type="button" className="transfer-deal-btn secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              className="transfer-deal-btn primary"
              disabled={loading || !targetLeadId}
            >
              {loading ? 'Transferring…' : 'Transfer project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
